import * as Yup from "yup";

/** Allowed symbols (printable ASCII punctuation per policy). */
export const PASSWORD_SYMBOLS = `!"#$%&'()*+,-./:;<=>?@[\\]^_\`{|}~`;

export const PASSWORD_MIN_LENGTH = 10;
export const PASSWORD_MAX_BYTES = 72;

const SYMBOL_SET = new Set(PASSWORD_SYMBOLS.split(""));

export const utf8ByteLength = (value) => {
  const str = String(value ?? "");
  if (typeof TextEncoder !== "undefined") {
    return new TextEncoder().encode(str).length;
  }
  return unescape(encodeURIComponent(str)).length;
};

/**
 * Returns an error message when the password fails policy, otherwise null.
 */
export const getPasswordError = (password, { required = true } = {}) => {
  if (password == null || password === "") {
    return required ? "Password is required" : null;
  }
  const value = String(password);
  if (value.length < PASSWORD_MIN_LENGTH) {
    return `Password must be at least ${PASSWORD_MIN_LENGTH} characters long`;
  }
  if (utf8ByteLength(value) > PASSWORD_MAX_BYTES) {
    return `Password must be at most ${PASSWORD_MAX_BYTES} bytes in UTF-8`;
  }
  if (!/[a-z]/.test(value)) {
    return "Password must include a lowercase letter (a-z)";
  }
  if (!/[A-Z]/.test(value)) {
    return "Password must include an uppercase letter (A-Z)";
  }
  if (!/[0-9]/.test(value)) {
    return "Password must include a digit (0-9)";
  }
  if (![...value].some((ch) => SYMBOL_SET.has(ch))) {
    return `Password must include a symbol from ${PASSWORD_SYMBOLS}`;
  }
  return null;
};

export const isPasswordValid = (password) => !getPasswordError(password);

export const yupPassword = (label = "Password") =>
  Yup.string()
    .required(`${label} is required`)
    .test("password-policy", "Invalid password", function (value) {
      const err = getPasswordError(value, { required: false });
      if (err) {
        return this.createError({ message: err });
      }
      return true;
    });

/** Compact hint under password fields (no heading). */
export const PASSWORD_HINT =
  `Use ${PASSWORD_MIN_LENGTH}–${PASSWORD_MAX_BYTES} characters with uppercase, lowercase, a number, and a symbol (!@#$%…).`;
